import type { FC } from 'react'

import { LoaderCircle, Table } from '@skytech/ui'
import { useQuery } from '@tanstack/react-query'

import { Translation } from '@/app/translations/Translation'
import { NoResults } from '@/components/NoResults'
import { ResultCount } from '@/components/ResultCount'
import { useAdminAccess } from '@/hooks/access'
import { useRenderLimit } from '@/hooks/use-render-limit'
import queries from '@/modules/admin/queries'
import type { Invoice } from '@/modules/admin/resources'
import adminResources from '@/modules/admin/resources'

import { InvoicesListItem } from './InvoicesListItem'

export const InvoicesList: FC<{
  searchFilter: string
  invoices: Invoice[]
}> = ({ searchFilter, invoices }) => {
  useAdminAccess()

  const { isLoading, isError } = useQuery(
    queries.adminInvoicesList(),
    adminResources.getInvoicesList
  )

  const { visibleList, ListLoaderComponent } = useRenderLimit(invoices || [], 30)

  if (isLoading) {
    return <LoaderCircle />
  }

  if (isError || invoices.length <= 0) {
    return <NoResults />
  }

  const isAnyFileToDownload = visibleList.some(el => el.availableForDownload)

  return (
    <>
      <ResultCount count={invoices.length} />
      <Table>
        <thead>
          <tr>
            <th>
              <Translation id="vendor" />
            </th>

            <th>
              <Translation id="invoice" />
            </th>

            <th className="hidden sm:table-cell">
              <Translation id="date" />
            </th>

            <th className="hidden sm:table-cell">
              <Translation id="due date" />
            </th>

            <th className="hidden pr-6 text-right sm:table-cell">
              <Translation id="amount" />
            </th>
            {isAnyFileToDownload && <th />}
          </tr>
        </thead>
        <tbody>
          {visibleList.map(item => (
            <InvoicesListItem
              key={item.id}
              searchFilter={searchFilter}
              isAnyFileToDownload={isAnyFileToDownload}
              {...item}
            />
          ))}
        </tbody>
      </Table>
      <ListLoaderComponent />
    </>
  )
}
